import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { LoadingState, LoadingStateOps } from "../../../domain/data-layer/loading-state";
import { FetchedDealersState } from "../state/use-fetched-dealers";
import React, { useEffect, useMemo } from "react";
import { StyledLoadingSpinner } from "../../../routes/modal-loading-spinner";

import { SerializedStyles } from "@emotion/react";
import { css } from "@emotion/react";

import { CloseButtonSvg } from "../../../assets";
import { DealerCardMainInfo } from "./DealerCardMainInfo";
import { DealerCardBottomInfo } from "./DealerCardBottomInfo";
import { getNavigationLink } from "../google-maps/get-navigation-link";
import { DealerInfoSummary, FullDealerInfoSummary } from "@swo/storefront";

import {
  MailSvg,
  PersonSvg,
  MobileSvg,
  PinSvg,
  WebsiteSvg,
} from "../../../layouts/shop-layouts/checkout-page-layout/CheckoutComponent/shared/form/FormSvgIcons";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { fontColor, FONTS, fontSize } from "../../../styles/fonts";
import { DealerWithCoordinatesT } from "../queries/get-dealers-by-query";

export const SelectedDealer: ReactTFC<{
  dealer: LoadingState<DealerWithCoordinatesT>;
  onClose: () => void;
  moreInfoLabel: string;
  translations: {
    birdingLabel: string;
    huntingLabel: string;
    outdoorLabel: string;
    premiumLabel: string;
  };
}> = props => {
  useEffect(() => {
    if (props.dealer.__tag == "error") {
      props.onClose();
    }
  }, [props.dealer.__tag]);

  if (props.dealer.__tag === "loading") {
    return (
      <span>
        <StyledLoadingSpinner />
      </span>
    );
  } else if (props.dealer.__tag === "loaded") {
    const dealerData = props.dealer.data;

    return (
      <div>
        <button
          type={"button"}
          onClick={props.onClose}
          css={css`
            width: 26px;
            height: 100%;

            display: block;
            margin-bottom: 10px;
            margin-left: auto;
          `}
        >
          <CloseButtonSvg
            css={css`
              width: 100%;
              height: 100%;
            `}
          />
        </button>

        <div>
          <DealerCardMainInfo
            headline={dealerData.name}
            segment={dealerData.segment}
            isPremium={!!dealerData.isPremium}
            translations={props.translations}
          />

          <DealerCardDetails
            representativeName={dealerData.contact.representativeName}
            address={dealerData.address.label}
            internationalPhoneNumber={dealerData.address.internationalPhoneNumber}
            email={dealerData.address.email}
            website={dealerData.contact.website}
            attachStyles={css`
              margin-top: 40px;
              margin-bottom: 50px;
            `}
          />

          <DealerCardBottomInfo
            email={dealerData.address.email}
            internationalPhoneNumber={dealerData.address.internationalPhoneNumber}
            navigationLink={getNavigationLink(dealerData.address.coordinates)}
            website={
              dealerData.swoWebsite
                ? {
                    link: dealerData.swoWebsite,
                    label: props.moreInfoLabel,
                  }
                : undefined
            }
          />
        </div>
      </div>
    );
  } else {
    return null;
  }
};

export const DealerCardDetails: ReactTFC<{
  representativeName: string | undefined | null;
  address: Array<string>;
  internationalPhoneNumber: string | undefined | null;
  email: string | undefined | null;
  website: string | undefined | null;
  attachStyles?: SerializedStyles;
}> = props => {
  const iconStyles = css`
    width: 24px;
    height: 24px;

    justify-content: center;
    align-items: center;
    align-self: center;
  `;

  const svgStyles = css`
    max-width: 100%;
    max-height: 100%;
  `;

  const textStyles = [FONTS.SwarovskiFutura300, fontColor.black, fontSize.x2];

  const linkStyles = [
    FONTS.SwarovskiFutura300,
    fontSize.x2,
    css`
      text-decoration: underline;
      color: ${CSS_COLORS.Green};
    `,
  ];

  return (
    <div
      css={css`
        display: grid;
        grid-template-columns: 40px 1fr;
        grid-row-gap: 30px;

        ${props.attachStyles};
      `}
    >
      {props.representativeName && (
        <React.Fragment>
          <span css={iconStyles}>
            <PersonSvg css={svgStyles} />
          </span>
          <span css={textStyles}>{props.representativeName}</span>
        </React.Fragment>
      )}

      <span css={iconStyles}>
        <PinSvg css={svgStyles} />
      </span>
      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        {props.address.map(line => (
          <span key={line} css={textStyles}>
            {line}
          </span>
        ))}
      </div>

      {props.internationalPhoneNumber && (
        <React.Fragment>
          <span css={iconStyles}>
            <MobileSvg css={svgStyles} />
          </span>
          <a css={linkStyles} href={`tel:${props.internationalPhoneNumber}`}>
            {props.internationalPhoneNumber}
          </a>
        </React.Fragment>
      )}

      {props.email && (
        <React.Fragment>
          <span css={iconStyles}>
            <MailSvg css={svgStyles} />
          </span>
          <a css={linkStyles} href={`mailto:${props.email}`}>
            {props.email}
          </a>
        </React.Fragment>
      )}

      {props.website && (
        <React.Fragment>
          <span css={iconStyles}>
            <WebsiteSvg css={svgStyles} />
          </span>
          <a
            css={linkStyles}
            href={ensureLinkWithHttpProtocol(props.website)}
            target={"_blank"}
            rel={"noopener noreferrer"}
          >
            {props.website}
          </a>
        </React.Fragment>
      )}
    </div>
  );
};

function ensureLinkWithHttpProtocol(url: string) {
  if (url.startsWith("http")) {
    return url;
  } else {
    return `http://${url}`;
  }
}
